import { type FC, useCallback } from 'react';

import { useInternalScrollCarousel } from 'dibs-scroll-carousel/exports/InternalScrollCarouselContext.ts';
import { type OnArrowClick } from 'dibs-scroll-carousel/exports/types.ts';
import { defineMessages, useIntl } from 'dibs-react-intl';
import dibsCss from 'dibs-css';
import classnames from 'classnames';

import { CarouselArrowButton } from 'dibs-scroll-carousel/exports/CarouselArrowButton.ts';

import styles from './HeroCarouselArrow.scss';

const messages = defineMessages({
    previousImage: {
        id: 'abf.HeroCarouselArrow.previousImage',
        defaultMessage: 'Previous image',
    },
    nextImage: {
        id: 'abf.HeroCarouselArrow.nextImage',
        defaultMessage: 'Next image',
    },
});

export const HeroCarouselArrow: FC<{
    type: 'previous' | 'next';
    onArrowClick?: OnArrowClick;
}> = ({ type, onArrowClick }) => {
    const intl = useIntl();
    const {
        currentIndex,
        stepSize,
        totalItems,
        isInfinite,
        itemsToShow,
        hasMultipleSlides,
        setCurrentIndex,
    } = useInternalScrollCarousel();

    const isPrev = type === 'previous';

    const onClick = useCallback(() => {
        setCurrentIndex(isPrev ? currentIndex - stepSize : currentIndex + stepSize);
        onArrowClick?.({ direction: isPrev ? 'previous' : 'next' });
    }, [isPrev, currentIndex, stepSize, setCurrentIndex, onArrowClick]);

    const endIndex = totalItems - 1;
    const showPrevArrow = currentIndex + (totalItems % stepSize) - stepSize >= 0;
    const showNextArrow = currentIndex + Math.floor(itemsToShow) <= endIndex;

    const shouldShow =
        (isInfinite && hasMultipleSlides) ||
        (!isInfinite && hasMultipleSlides && (isPrev ? showPrevArrow : showNextArrow));

    if (!shouldShow) {
        return null;
    }

    return (
        <CarouselArrowButton
            direction={isPrev ? 'left' : 'right'}
            size="large"
            onClick={onClick}
            dataTn={`arrow-${isPrev ? 'left' : 'right'}`}
            ariaLabel={intl.formatMessage(isPrev ? messages.previousImage : messages.nextImage)}
            className={classnames(dibsCss.top50, isPrev ? styles.arrowLeft : styles.arrowRight)}
        />
    );
};
